import { Alert, Box, IconButton, Skeleton, Tooltip } from '@mui/material';
import React, { useCallback, type ReactNode } from 'react';
import LegacyAttributesSidePanel from '../../../../attributes/AttributesSidePanel';
import type { EntityName, EntitySecurity } from '../../../auth/Security';
import { EntitySecurityProvider, SECURITY_ACTIONS, useSecurity } from '../../../auth/Security';
import I18n, { useI18n } from '../../../i18n/I18n';
import { Icons } from '../../../icons/Icons';
import { PanelNoAccess } from '../../ui/components/PanelNoAccess';
import { usePanelCommands } from '../../ui/hooks/usePanelCommands';
import { usePanelSession } from '../../ui/hooks/usePanelSession';
import { useAttributesPanelData } from './data/useAttributesPanelData';
import type { AttributesPanelOptions } from './types';
import { useAttributesPanelSave } from './ui/useAttributesPanelSave';

const STATUS_SX = { p: 2 };
const CLOSE_SX = { display: 'flex', justifyContent: 'flex-end' };

function AttributesPanelStatus({ pending = false, message }: { pending?: boolean; message?: ReactNode }) {
    const { close } = usePanelCommands();
    const closeLabel = useI18n('dialog.close');
    const handleClose = useCallback(() => {
        close();
    }, [close]);
    return (
        <Box sx={STATUS_SX}>
            <Box sx={CLOSE_SX}>
                <Tooltip title={closeLabel}>
                    <IconButton aria-label={closeLabel} data-testid="attributes-panel-close" onClick={handleClose}>
                        <Icons.Close />
                    </IconButton>
                </Tooltip>
            </Box>
            {pending ? (
                <Skeleton data-testid="attributes-panel-pending" height={160} />
            ) : message ? (
                <Alert severity="error">{message}</Alert>
            ) : (
                <PanelNoAccess />
            )}
        </Box>
    );
}

function LoadedAttributesPanel({
    entityName,
    entityId,
    entityLabel,
    resourceSource
}: AttributesPanelOptions & { entityName: EntityName; entityId: string }) {
    const { attributesData, isLoadingAttributes, errorLoadingAttributes, isSupported } = useAttributesPanelData(
        entityName,
        entityId,
        resourceSource
    );
    const { close } = usePanelCommands();
    const handleClose = useCallback(() => {
        close();
    }, [close]);
    const saveAttributes = useAttributesPanelSave();
    const recordSecurity: EntitySecurity = attributesData?.security ?? {};
    const decision = useSecurity(SECURITY_ACTIONS.EDIT, { entityName, entitySecurity: recordSecurity });

    if (!isSupported) return <AttributesPanelStatus message={<I18n token="resource.load.failure" />} />;
    if (isLoadingAttributes || decision.pending) return <AttributesPanelStatus pending />;
    if (errorLoadingAttributes || !attributesData)
        return <AttributesPanelStatus message={<I18n token="resource.load.failure" />} />;
    if (!decision.allowed) return <AttributesPanelStatus />;

    return (
        <EntitySecurityProvider entityName={entityName} entitySecurity={recordSecurity}>
            <LegacyAttributesSidePanel
                entityId={entityId}
                entity={entityName}
                entityName={entityLabel ?? attributesData.name ?? ''}
                entityValues={attributesData}
                onClose={handleClose}
                onSaveEntity={saveAttributes}
            />
        </EntitySecurityProvider>
    );
}

export function AttributesSidePanel({ entityLabel, resourceSource = 'production' }: AttributesPanelOptions) {
    const { subject, sessionId } = usePanelSession();
    const decision = useSecurity(SECURITY_ACTIONS.EDIT, {
        entityName: subject?.entityName,
        entitySecurity: subject?.security
    });
    if (decision.pending) return <AttributesPanelStatus pending />;
    if (!subject?.reference.id || !decision.allowed) return <AttributesPanelStatus />;
    return (
        <LoadedAttributesPanel
            key={`${sessionId}:${subject.entityName}:${subject.reference.id}:${resourceSource}`}
            entityName={subject.entityName}
            entityId={subject.reference.id}
            entityLabel={entityLabel ?? subject.label ?? subject.reference.label}
            resourceSource={resourceSource}
        />
    );
}
